import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Fab from "@mui/material/Fab";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Alert from "@mui/material/Alert";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PAGE_SIZE } from "../../constants/pdm";
import { createDsApp, getDsApps } from "../../services/pdmApi.service";
import type { CreateDsAppPayload } from "../../types/pdm.types";
import { isRepositoryRemoteRef } from "../../utils/repositoryRemote";
import { DsAppDetailsDialog } from "./DsAppDetailsDialog";

const emptyForm: CreateDsAppPayload = {
  name: "",
  version: "",
  description: "",
  repositoryUrl: "",
  gitBranch: "",
};

export default function DsAppsView() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [selectedDsAppId, setSelectedDsAppId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState<CreateDsAppPayload>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);

  const dsAppsQuery = useQuery({
    queryKey: ["dsApps", page],
    queryFn: () =>
      getDsApps({
        offset: page * PAGE_SIZE,
        limit: PAGE_SIZE,
      }),
  });

  const createMutation = useMutation({
    mutationFn: (payload: CreateDsAppPayload) => createDsApp(payload),
    onSuccess: () => {
      setAddOpen(false);
      setForm(emptyForm);
      setFormError(null);
      void queryClient.invalidateQueries({ queryKey: ["dsApps"] });
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error ? err.message : String(err ?? "Request failed");
      setFormError(message);
    },
  });

  const rows = dsAppsQuery.data?.items ?? [];
  const total = dsAppsQuery.data?.total ?? 0;
  const detailsOpen = Boolean(selectedDsAppId);

  const handleAddClose = () => {
    if (!createMutation.isPending) {
      setAddOpen(false);
      setForm(emptyForm);
      setFormError(null);
    }
  };

  const handleSubmitAdd = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const trimmed: CreateDsAppPayload = {
      name: form.name.trim(),
      version: form.version.trim(),
      description: form.description.trim(),
      repositoryUrl: form.repositoryUrl.trim(),
      gitBranch: form.gitBranch.trim(),
    };
    const missing = Object.entries(trimmed).filter(([, v]) => !v);
    if (missing.length) {
      setFormError("All fields are required.");
      return;
    }
    if (!isRepositoryRemoteRef(trimmed.repositoryUrl)) {
      setFormError(
        "Repository must be an http(s) URL or an SSH-style clone URL (for example git@github.com:org/repo.git).",
      );
      return;
    }
    createMutation.mutate(trimmed);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ color: "brand.main" }}>
        DSApps
      </Typography>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Branch</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.version}</TableCell>
                <TableCell>{row.gitBranch}</TableCell>
                <TableCell>
                  {new Date(row.updateAt).toLocaleString(undefined, {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="View DSApp details">
                    <IconButton
                      size="small"
                      aria-label="View DSApp details"
                      onClick={() => setSelectedDsAppId(row.id)}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && !dsAppsQuery.isPending && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography color="text.secondary">
                    No DSApps match the current page.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
        />
      </TableContainer>

      <DsAppDetailsDialog
        open={detailsOpen}
        dsAppId={selectedDsAppId}
        onClose={() => setSelectedDsAppId(null)}
      />

      <Dialog
        open={addOpen}
        onClose={handleAddClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add DSApp</DialogTitle>
        <Box component="form" onSubmit={handleSubmitAdd}>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              {formError && <Alert severity="error">{formError}</Alert>}
              <TextField
                required
                label="Name"
                fullWidth
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
              <TextField
                required
                label="Version"
                fullWidth
                value={form.version}
                onChange={(e) =>
                  setForm((f) => ({ ...f, version: e.target.value }))
                }
              />
              <TextField
                required
                label="Description"
                fullWidth
                multiline
                minRows={2}
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
              />
              <TextField
                required
                label="Repository (HTTPS or SSH)"
                placeholder="https://github.com/org/repo.git or git@github.com:org/repo.git"
                helperText="Use an HTTPS/Git URL or the SSH remote you pass to git clone."
                fullWidth
                value={form.repositoryUrl}
                onChange={(e) =>
                  setForm((f) => ({ ...f, repositoryUrl: e.target.value }))
                }
              />
              <TextField
                required
                label="Git branch"
                fullWidth
                value={form.gitBranch}
                onChange={(e) =>
                  setForm((f) => ({ ...f, gitBranch: e.target.value }))
                }
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAddClose} disabled={createMutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={createMutation.isPending}>
              {createMutation.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Fab
        color="primary"
        variant="extended"
        sx={{ position: "fixed", bottom: 24, right: 24 }}
        onClick={() => setAddOpen(true)}
      >
        <AddIcon sx={{ mr: 1 }} />
        Add DSApp
      </Fab>
    </Box>
  );
}
